import {createContext, ReactNode, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {useApiService} from "../hooks/useApiService"
import {useAuth} from "../hooks/useAuth"
import {useFeedback} from "../hooks/useFeedback"
import {AnalyzeTarget, analyzeMatch, resolveEventTeam} from "../services/eventSearch"
import {recordAnalysis} from "../services/analysisHistory"
import {Event, EventTeam} from "../types/event"
import {PredictionAccessDenial} from "../types/subscription"

export const IAnalyzeContext = createContext<AnalyzeContextState>({})

export type AnalyzeStep =
    "idle"
    | "matching_teams"
    | "fetching_predictions"
    | "done"
    | "error"

export type AnalyzeProgress = {
    step: AnalyzeStep
    /** 0 to 100, drives the inline progress ring */
    percent: number
    label: string
}

const RUNNING_STEPS: AnalyzeStep[] = ["matching_teams", "fetching_predictions"]

/**
 * Why the API refused to analyse, when it did.
 *
 * Kept apart from `error`: a refusal is not a failure to be retried, it is a plan to be bought, and
 * the page answers it with the pricing rather than with an error line.
 */
export type AnalyzeDenial = {
    reason: PredictionAccessDenial
    /** Midnight the daily quota comes back on, when the quota is what ran out */
    resetAt?: number
}

export type {AnalyzeTarget}

export type AnalyzeContextState = {
    progress?: AnalyzeProgress
    loading?: boolean
    error?: string
    denial?: AnalyzeDenial
    /** The most recent event returned by the prediction api for the analyzed pair */
    event?: Event
    events?: Event[]
    teams?: { home?: EventTeam, away?: EventTeam }
    /** Analyses left today, as counted by the API on the last one it served */
    remainingToday?: number
    analyze?: (home: AnalyzeTarget, away: AnalyzeTarget) => Promise<Event | void>
    cancel?: () => void
    reset?: () => void
}

const IDLE_PROGRESS: AnalyzeProgress = {step: "idle", percent: 0, label: ""}

/** A refusal the API explains, as opposed to something that went wrong on the way */
const denialOf = (reason: any): AnalyzeDenial | undefined => {
    const denial = reason?.reason || reason?.messageCode

    return reason?.status === 403 && ["limit_reached", "subscription_expired", "no_subscription"].includes(denial)
        ? {reason: denial, resetAt: reason?.resetAt}
        : undefined
}

const AnalyzeContext = (props: { children: ReactNode }) => {
    const apiService = useApiService()
    const auth = useAuth()
    const feedback = useFeedback()
    const {t} = useTranslation()
    const [progress, setProgress] = useState<AnalyzeProgress>(IDLE_PROGRESS)
    const [error, setError] = useState<string>()
    const [denial, setDenial] = useState<AnalyzeDenial>()
    const [teams, setTeams] = useState<{ home?: EventTeam, away?: EventTeam }>()
    const [events, setEvents] = useState<Event[]>()
    const [remainingToday, setRemainingToday] = useState<number>()
    // Bumped on every run so a cancelled or superseded analysis stops writing to the state
    const runRef = useRef(0)

    /**
     * Every call gets its own client because the api services write their params on client.defaults.
     * Signed in: analyses are charged to the account, so the API will not serve one anonymously.
     */
    const newClient = () => apiService.client(true, false)

    const reset = () => {
        runRef.current += 1
        setProgress(IDLE_PROGRESS)
        setError(undefined)
        setDenial(undefined)
        setTeams(undefined)
        setEvents(undefined)
        feedback.hideFeedback?.()
    }

    const cancel = () => {
        runRef.current += 1
        setProgress(IDLE_PROGRESS)
        feedback.hideFeedback?.()
    }

    const analyze = async (home: AnalyzeTarget, away: AnalyzeTarget): Promise<Event | void> => {
        const run = ++runRef.current
        const isStale = () => run !== runRef.current

        const advance = (step: AnalyzeStep, percent: number, label: string) => {
            if (isStale()) return
            setProgress({step, percent, label})
        }

        const fail = (message: string) => {
            if (isStale()) return
            setProgress({step: "error", percent: 0, label: message})
            setError(message)
            feedback.hideFeedback?.()
            feedback.showFeedback?.(message, "danger")
        }

        const refuse = (denied: AnalyzeDenial) => {
            if (isStale()) return
            setProgress(IDLE_PROGRESS)
            setDenial(denied)
            feedback.hideFeedback?.()
        }

        setError(undefined)
        setDenial(undefined)
        setTeams(undefined)
        setEvents(undefined)

        if (!home?.name || !away?.name) {
            fail(t("pickBothTeams"))
            return
        }

        try {
            advance("matching_teams", 30, t("matchingTeams", {home: home.name, away: away.name}))
            const [homeTeam, awayTeam] = await Promise.all([
                resolveEventTeam(newClient, home),
                resolveEventTeam(newClient, away)
            ])
            if (isStale()) return

            const unmatched = [!homeTeam && home.name, !awayTeam && away.name].filter(Boolean)
            if (unmatched.length) {
                fail(t("noExactMatch", {teams: unmatched.join(", ")}))
                return
            }
            setTeams({home: homeTeam, away: awayTeam})

            /**
             * One call from here on: the fixture may be scheduled the other way around, and it is
             * the API that looks the reverse up — the analysis is charged where it is served, so it
             * cannot be assembled from calls the client makes at its own discretion.
             */
            advance("fetching_predictions", 75, t("retrievingPredictions"))
            const response = await analyzeMatch(newClient, homeTeam.id, awayTeam.id)
            if (isStale()) return

            const found = response?.data || []

            if (!found.length) {
                fail(t("noUpcomingBetween", {home: homeTeam.name, away: awayTeam.name}))
                return
            }

            setEvents(found)
            setRemainingToday(response?.remainingToday)
            setProgress({step: "done", percent: 100, label: t("analysisReady")})
            feedback.hideFeedback?.()
            // Kept so the history page can reopen it without spending another analysis
            recordAnalysis(auth.user?.id || auth.user?._id, found[0])
            return found[0]
        } catch (reason) {
            const denied = denialOf(reason)

            if (denied)
                return refuse(denied)

            fail(reason?.message || t("analysisFailed"))
        }
    }

    const state: AnalyzeContextState = {
        progress,
        loading: RUNNING_STEPS.includes(progress.step),
        error,
        denial,
        event: events?.[0],
        events,
        teams,
        remainingToday,
        analyze,
        cancel,
        reset
    }

    return <IAnalyzeContext.Provider value={state}>
        {props.children}
    </IAnalyzeContext.Provider>
}

export default AnalyzeContext
